import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Email, OrganizationId, SessionId, VaultId } from 'backend-client-web';

type RecentActionIdTypeMap = {
  teamCreated: VaultId;
  teamJoined: VaultId;
  organizationCreated: OrganizationId;
  organizationCouponRedeemed: OrganizationId;
  sessionPaired: SessionId;
};

type RecentActionDataTypeMap = {
  teamJoined: { addedByEmail: Email };
  sessionPaired: { sessionName: string };
};

type RecentActionName =
  | 'teamCreated'
  | 'teamJoined'
  | 'organizationCreated'
  | 'organizationCouponRedeemed'
  | 'sessionPaired';
type RecentActionIdType<T extends RecentActionName> = RecentActionIdTypeMap[T];
type RecentActionDataType<T extends RecentActionName> =
  T extends keyof RecentActionDataTypeMap ? RecentActionDataTypeMap[T] : undefined;

type RecentAction<A extends RecentActionName> = {
  id: RecentActionIdType<A>;
  data: RecentActionDataType<A>;
};

type RecentActionIds = {
  [A in RecentActionName]?: RecentAction<A>;
};

type SetRecentActionId<A extends RecentActionName> =
  A extends keyof RecentActionDataTypeMap ?
    (value: RecentActionIdType<A>, data: RecentActionDataType<A>) => void
  : (value: RecentActionIdType<A>) => void;

interface RecentActionIdContextValue {
  recentActionIds: RecentActionIds;
  setRecentActionIds: Dispatch<SetStateAction<RecentActionIds>>;
}

const RecentActionIdContext = createContext<RecentActionIdContextValue>({
  recentActionIds: {},
  setRecentActionIds: () => {},
});

export function RecentActionIdContextProvider({ children }: PropsWithChildren<{}>) {
  const [recentActionIds, setRecentActionIds] = useState<RecentActionIds>({});

  return (
    <RecentActionIdContext.Provider value={{ recentActionIds, setRecentActionIds }}>
      {children}
    </RecentActionIdContext.Provider>
  );
}

export function useSetRecentActionId<A extends RecentActionName>(name: A): SetRecentActionId<A> {
  const { setRecentActionIds } = useContext(RecentActionIdContext);

  return useCallback(
    (id: RecentActionIdType<A>, data?: RecentActionDataType<A>) => {
      setRecentActionIds((ids) => ({ ...ids, [name]: { id, data } }));
    },
    [name, setRecentActionIds],
  ) as SetRecentActionId<A>;
}

export function useRecentActionPerformed<A extends RecentActionName>(
  name: A,
  id: RecentActionIdType<A>,
): ({ value: true; data: RecentActionDataType<A> } | { value: false; data: undefined }) & {
  reset: () => void;
} {
  const { recentActionIds, setRecentActionIds } = useContext(RecentActionIdContext);
  const recentAction = recentActionIds[name];

  const reset = useCallback(() => {
    if (recentAction?.id !== id) {
      return;
    }
    setRecentActionIds((ids) => ({ ...ids, [name]: undefined }));
  }, [recentAction?.id, id, setRecentActionIds, name]);

  if (recentAction?.id !== id) {
    return { value: false, data: undefined, reset } as const;
  }
  return { value: true, data: recentAction.data, reset };
}

export function useRecentActionPerformedClearOnRender<A extends RecentActionName>(
  name: A,
  id: RecentActionIdType<A>,
) {
  const recentActionPerformed = useRecentActionPerformed(name, id);
  const { reset: recentActionPerformedReset, ...recentActionPerformedRest } = recentActionPerformed;
  const [recentActionPerformedWithDelayedReset, setRecentActionPerformedWithDelayedReset] =
    useState({ ...recentActionPerformedRest, id });

  useEffect(() => {
    recentActionPerformed.reset();

    if (recentActionPerformed.value || recentActionPerformedWithDelayedReset.id !== id) {
      setRecentActionPerformedWithDelayedReset({ ...recentActionPerformed, id });
    }
  }, [recentActionPerformed, id, recentActionPerformedWithDelayedReset.id]);

  const reset = useCallback(() => {
    recentActionPerformedReset();
    setRecentActionPerformedWithDelayedReset({ value: false, data: undefined, id });
  }, [id, recentActionPerformedReset]);

  return { ...recentActionPerformedWithDelayedReset, reset };
}
